import React, { useState, useEffect, useCallback, useRef } from "react";
import { useHelpModal } from "./HelpModalContext";
import { FaTimes, FaDownload, FaCopy } from "react-icons/fa";

const AboutModal = ({ isOpen, onClose, appName = "AI Agent Portal" }) => {
  const { openHelpModal } = useHelpModal();
  const [packages, setPackages] = useState([]);
  const [version, setVersion] = useState("N/A");
  const [sessionToken, setSessionToken] = useState("");
  const [userOid, setUserOid] = useState("");
  const modalRef = useRef(null);

  useEffect(() => {
    fetch("/package-meta.json")
      .then((response) => response.json())
      .then((data) => {
        setVersion(data.v);
        const formattedPackages = Object.entries(data.dependencies).map(
          ([name, info]) => ({
            name: name.replace(/@/g, "").replace(/[-\/]/g, " "),
            version: info.v,
            license: info.l || "N/A",
          })
        );
        setPackages(formattedPackages);
      })
      .catch((error) => console.error("Error fetching package data:", error));

    const storedSessionToken =
      sessionStorage.getItem("x-session-token") || "Not available";
    const storedUserOid = sessionStorage.getItem("x-oid") || "Not available";
    setSessionToken(storedSessionToken);
    setUserOid(storedUserOid);
  }, []);

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    },
    [onClose]
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
    }
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, handleKeyDown]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };
    const handlePopState = () => {
      if (isOpen) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      window.addEventListener("popstate", handlePopState);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("popstate", handlePopState);
    };
  }, [isOpen, onClose]);

  const downloadLicenses = () => {
    const csvContent =
      "Package,Version,License\n" +
      packages
        .map((pkg) => `"${pkg.name}","${pkg.version}","${pkg.license}"`)
        .join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", "third_party_licenses.csv");
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {});
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-[10000]">
      <div
        ref={modalRef}
        className="bg-white bg-opacity-20 backdrop-blur-xl p-4 rounded-lg shadow-xl w-[600px] h-[60vh] overflow-hidden border border-white border-opacity-20 relative flex flex-col"
      >
        <div className="flex justify-between items-center mb-2">
          <div className="flex items-center">
            <img src="/logo.svg" alt="Logo" className="h-6 mr-2" />
            <h2 className="text-lg font-bold text-white font-sol-w01">
              {appName}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-300 transition-colors duration-200"
          >
            <FaTimes size={18} />
          </button>
        </div>
        <div className="flex justify-between items-center mb-2 text-xs">
          <p className="text-white">Build: {version}</p>
        </div>
        <div className="flex items-center mb-2">
          <h3 className="text-base font-semibold text-white mr-2">
            Third-Party Software Licenses
          </h3>
          <button
            onClick={downloadLicenses}
            className="text-blue-300 hover:text-green-100 transition-colors duration-200"
            title="Download Licenses"
          >
            <FaDownload size={14} />
          </button>
        </div>
        <div className="grid grid-cols-6 gap-2 mb-1 text-xs">
          <div className="font-bold text-white ml-1 col-span-4">Package</div>
          <div className="font-bold text-white ml-[-16px]">Version</div>
          <div className="font-bold text-white ml-[-20px]">License</div>
        </div>
        <div className="overflow-auto flex-grow scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-transparent">
          <div className="grid grid-cols-6 gap-2 px-2 text-xs">
            {packages.map((pkg, index) => (
              <React.Fragment key={index}>
                <div className="capitalize text-white col-span-4">
                  {pkg.name}
                </div>
                <div className="text-white">{pkg.version}</div>
                <div className="text-white">{pkg.license}</div>
              </React.Fragment>
            ))}
          </div>
        </div>
        <div className="mt-2 pt-2 border-t border-white border-opacity-20 text-xs text-gray-400">
          <div className="grid grid-cols-6 items-center mb-1 gap-1">
            <span>Session Token</span>
            <span className="col-span-3 font-mono">{sessionToken}</span>
            <button
              onClick={() => copyToClipboard(sessionToken)}
              className="hover:text-gray-500 transition-colors duration-200 col-span-2"
              title="Copy Session Token"
            >
              <FaCopy size={14} />
            </button>
            <span>User OID</span>
            <span className="col-span-3 font-mono">{userOid}</span>
            <button
              onClick={() => copyToClipboard(userOid)}
              className="hover:text-gray-500 transition-colors duration-200 col-span-2"
              title="Copy User OID"
            >
              <FaCopy size={14} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutModal;
